import { Building2, MoreVertical, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Participants, type Participant } from './Participants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface MeetingCardProps {
  subject?: string;
  children?: React.ReactNode;
  formattedDate?: string;
  accountName?: string;
  participants: Participant[];
  url?: string;
}

export function MeetingCard({
  subject,
  formattedDate,
  participants,
  children,
  url,
  accountName,
}: MeetingCardProps) {
  const { t } = useTranslation('prince');

  const showHeader = subject || formattedDate || url;
  return (
    <div className="flex flex-col self-stretch gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
      {showHeader && (
        <div className="flex justify-between items-center self-stretch">
          {subject && (
            <h3 className="font-semibold text-sm leading-tight text-gray-900 m-0 flex-1">
              {subject}
            </h3>
          )}

          <div className="flex items-center gap-1">
            {formattedDate && (
              <span
                className="font-normal text-xs leading-tight text-gray-600 text-right w-24"
                data-testid="engagement-date"
              >
                {formattedDate}
              </span>
            )}

            {url && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                    aria-label="More options"
                    data-testid="engagement-menu-trigger"
                  >
                    <MoreVertical className="w-4 h-4 text-gray-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    onClick={() => window.open(url, '_blank')}
                    className="cursor-pointer"
                    data-testid="engagement-view-link"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('toolCalls.chorus.navigateToEngagement')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      )}

      {children && (
        <span className="font-normal text-sm leading-relaxed text-gray-700 m-0 self-stretch">
          {children}
        </span>
      )}

      {/* Tags section */}
      <div className="flex items-center gap-x-5 gap-y-2 self-start flex-wrap">
        {/* Account tag */}
        {accountName && (
          <div
            className="flex items-center gap-2 px-2 bg-purple-50 border border-purple-200 rounded text-purple-700"
            data-testid="engagement-account-name"
          >
            <Building2 className="w-3 h-3 text-purple-500 shrink-0" />
            {accountName}
          </div>
        )}

        {/* Participants tag */}
        {participants.length > 0 && <Participants participants={participants} />}
      </div>
    </div>
  );
}
